/**
 * @lines98/client/components/auth-panel — переиспользуемая панель «Аккаунт»
 * (ADR-0006 rev3 + spec email-registration.md §2).
 *
 * ЗАЧЕМ / WHY THIS EXISTS: правило №1 «ствол → ветки» + spec §2.0 R-6: форма-логика
 * входа/регистрации (3 кнопки Google/Discord/Почта + раскрываемая inline-форма с
 * переключателем Вход/Регистрация, 4 состояния, RU-маппинг ошибок, show/hide пароля)
 * — ОДИН переиспользуемый модуль-примитив, а НЕ верстается в экране. Тело модалки входа
 * (`lib/auth-modal`) монтирует его тонкой веткой. Поля/валидация НЕ дублируются по
 * экранам (§2.0). Описательный текст (заголовок/пояснения/«?») УБРАН — owner-directive
 * 2026-06-12 («только кнопки + форма»).
 *
 * РАЗМЕР ТЕКСТ-КОНТРОЛОВ — group-uniform autofit ВСЕЙ ПЛАШКИ (owner-law 2026-06-12,
 * skill §8.10-B): 3 кнопки провайдеров + переключатель Вход/Регистрация + CTA формы =
 * ОДНА группа → один ИЗМЕРЕННЫЙ размер = min. Правило ВЛАДЕЕТ примитив-плашка
 * (dialog-plaque.attachPlaqueGroupAutofit) — панель лишь отдаёт ей актуальный состав
 * группы (collectGroupMembers). Поштучный autofit/статичный font-size PROHIBITED.
 *
 * ГОСТЕВОЕ ТЕЛО (account-profile-redesign.md §0/§4): панель рендерит ТОЛЬКО гостевое
 * состояние (3 кнопки + раскрываемая форма). Состояние «залогинен» (ник + method +
 * masked email + «Выйти») УДАЛЕНО — выход теперь живёт в меню account-chip (§3),
 * вход — в auth-modal. Панель больше не держит identity/logout.
 *
 * SCOPE: фабрика `mountAuthPanel(host, opts) → handle`. a11y + размеры-токены зашиты
 * ВНУТРЬ (code-structure §2): все размеры от `--btn-h` / `--fs-*` / `--m` / `--r*`
 * (cell-производные, owner-directive 2026-06-09 «от cell-size, без хардкода/капов» —
 * см. auth-panel.css). Сетевые вызовы инъектируются (onGoogle/onDiscord/register/
 * login) → панель не привязана к конкретному сервису → тестируема + reusable.
 * NOT IN SCOPE: сами сетевые сервисы (services/local-auth, oauth-login), состояние
 * «залогинен»/выход (меню account-chip), монтаж на экран.
 *
 * Cross-references:
 *   - spec §2.1–§2.4 (гостевое поведение), §2.4 (RU-маппинг), §6.0 (must-visible / popover), AC-1..AC-9/AC-16/AC-17.
 *   - account-profile-redesign.md §0/§4 (тело auth-modal; logged-in-state удалён).
 *   - components/segmented (Вход/Регистрация — selfAutofit:false: размером правит плашка),
 *     components/dialog-plaque (attachPlaqueGroupAutofit — group-uniform правило плашки),
 *     components/pill-button (провайдеры + CTA с ICON_LOGIN), icon-button (глаз show/hide).
 *   - services/local-auth (RegisterResult / LoginResult — формы reason), oauth-login.
 *   - auth-panel.css (co-located; sizing algebra + layout-plan).
 */

import './auth-panel.css';

import { t } from '../lib/i18n/index.js';
import { createSegmented, type SegmentedHandle } from './segmented.js';
import { createPillButton } from './pill-button.js';
import {
  attachPlaqueGroupAutofit,
  type PlaqueAutofitHandle,
} from './dialog-plaque.js';
import type { GroupMember } from '../lib/autofit-text.js';
import type {
  LoginResult,
  RegisterResult,
  RegisterErrorReason,
  LoginErrorReason,
} from '../services/local-auth.js';

// =============================================================================
// RU-маппинг серверных reason → человекочитаемый текст (spec §2.4). UI НИКОГДА
// не показывает сырые коды.
// =============================================================================

/** Поле, к которому привязать сообщение (или 'form' = над CTA). */
type ErrorSlot = 'email' | 'password' | 'displayName' | 'form';

interface MappedError {
  readonly slot: ErrorSlot;
  readonly text: string;
  /** Регистрация email_taken → действие «Перейти ко входу» (§2.4 / AC-5). */
  readonly offerSwitchToLogin?: boolean;
}

// Маппинги строятся ЛЕНИВО (функция, не module-const): текст резолвится `t()` в
// момент показа ошибки (submit) → язык берётся актуальный, а не запечённый на import
// (overlay открывается после смены языка — каталог уже подгружен). `weak_password`
// НЕ в каталоге ключей (extraction-gap) → остаётся литералом до заведения ключа.
function registerErrorMap(): Record<RegisterErrorReason, MappedError> {
  return {
    invalid_email: { slot: 'email', text: t('auth.panel.errEmailMalformed') },
    weak_password: {
      slot: 'password',
      text: t('auth.panel.errWeakPassword'),
    },
    invalid_display_name: { slot: 'displayName', text: t('auth.panel.errNickInvalid') },
    email_taken: {
      slot: 'form',
      text: t('auth.panel.errEmailTaken'),
      offerSwitchToLogin: true,
    },
    no_identity: { slot: 'form', text: t('auth.panel.errStartFailed') },
    unauthorized: { slot: 'form', text: t('auth.panel.errStartFailed') },
    unavailable: { slot: 'form', text: t('auth.panel.errUnavailable') },
    server_error: { slot: 'form', text: t('auth.panel.errServer') },
    network: { slot: 'form', text: t('auth.panel.errNetwork') },
  };
}

function loginErrorMap(): Record<LoginErrorReason, MappedError> {
  return {
    // Обезличено (AC-6) — НЕ раскрываем, что именно неверно.
    credentials_invalid: { slot: 'form', text: t('auth.panel.errCredentials') },
    malformed_request: { slot: 'form', text: t('auth.panel.errMalformedRequest') },
    unavailable: { slot: 'form', text: t('auth.panel.errUnavailable') },
    server_error: { slot: 'form', text: t('auth.panel.errServer') },
    network: { slot: 'form', text: t('auth.panel.errNetwork') },
  };
}

// =============================================================================
// Бренд-цвета провайдер-иконок (owner-decision 2026-06-12 A3). Кнопки нейтральные,
// различаются ТОЛЬКО цветной ведущей иконкой → цвет несёт сам SVG (НЕ currentColor),
// именованными константами (не разбросанный magic-hex). Google — 4 фирменных бренд-
// цвета; Discord — фирменный блюрпл; Почта — accent-токен сайта (через currentColor
// иконки, перекрашенной в --accent классом .auth-panel-provider--email в CSS).
// =============================================================================

/** Google brand palette (официальные «G»-цвета). */
const GOOGLE_BLUE = '#4285F4';
const GOOGLE_GREEN = '#34A853';
const GOOGLE_YELLOW = '#FBBC05';
const GOOGLE_RED = '#EA4335';
/** Discord brand blurple. */
const DISCORD_BLURPLE = '#5865F2';

// =============================================================================
// SVG-глифы провайдеров (ведущие иконки кнопок) + глаз show/hide.
// =============================================================================

/** Google «G» — многоцветная (4 фирменных пути с бренд-цветами). */
const ICON_GOOGLE = `<svg viewBox="0 0 24 24" stroke="none" aria-hidden="true" focusable="false"><path fill="${GOOGLE_BLUE}" d="M23.52 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.88c2.27-2.09 3.57-5.17 3.57-8.87z"/><path fill="${GOOGLE_GREEN}" d="M12 24c3.24 0 5.96-1.08 7.95-2.91l-3.88-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.27v3.09A12 12 0 0 0 12 24z"/><path fill="${GOOGLE_YELLOW}" d="M5.27 14.29a7.2 7.2 0 0 1 0-4.58V6.62H1.27a12 12 0 0 0 0 10.76l4-3.09z"/><path fill="${GOOGLE_RED}" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.43-3.43C17.95 1.19 15.24 0 12 0A12 12 0 0 0 1.27 6.62l4 3.09C6.22 6.86 8.87 4.75 12 4.75z"/></svg>`;

/** Discord (фирменный блюрпл, simple-icons single path). */
const ICON_DISCORD = `<svg viewBox="0 0 24 24" fill="${DISCORD_BLURPLE}" stroke="none" aria-hidden="true" focusable="false"><path d="M20.317 4.369a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>`;

/** Почта — Tabler mail (конверт, stroke; currentColor → --accent через CSS). */
const ICON_MAIL = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10z"/><path d="M3 7l9 6l9 -6"/></svg>`;

/**
 * Login (дверь + стрелка внутрь) — ведущий значок CTA «Войти»/«Создать аккаунт» (тот же
 * глиф ICON_LOGIN, что у рейловой пилюли «Войти», nav-rail.ts — единый знак входа).
 * currentColor → наследует цвет текста кнопки (CTA accent → белый). Submit-семантика
 * формы (вход/регистрация — оба «войти в аккаунт»).
 */
const ICON_LOGIN = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M14 8v-2a2 2 0 0 0 -2 -2h-7a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h7a2 2 0 0 0 2 -2v-2"/><path d="M21 12h-13l3 -3"/><path d="M11 15l-3 -3"/></svg>`;

/**
 * Create-account (силуэт + плюс) — ведущий значок CTA «Создать аккаунт» (режим
 * Регистрация). ДРУГОЙ глиф, чем у входа (owner-directive 2026-06-12: у «Создать аккаунт»
 * своё векторное изображение): регистрация = «новый аккаунт» (user-plus), вход = «дверь+
 * стрелка» (ICON_LOGIN). currentColor → белый на accent-CTA. Tabler user-plus.
 */
const ICON_USER_PLUS = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0"/><path d="M6 21v-2a4 4 0 0 1 4 -4h4"/><path d="M16 19h6"/><path d="M19 16v6"/></svg>`;

// =============================================================================
// SVG-глифы глаза show/hide (чёткое центрирование, не текстовый символ).
// =============================================================================

const ICON_EYE = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/><circle cx="12" cy="12" r="3"/></svg>`;
const ICON_EYE_OFF = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><path d="M1 1l22 22"/></svg>`;

// =============================================================================
// Types
// =============================================================================

export interface AuthPanelOptions {
  /** Запустить Google OAuth (redirect). */
  readonly onGoogle: () => void | Promise<void>;
  /** Запустить Discord OAuth (redirect). */
  readonly onDiscord: () => void | Promise<void>;
  /**
   * Регистрация email-аккаунта (services/local-auth.register). Вариант B (owner-decision
   * 2026-06-21): БЕЗ ника — ник задаётся в Профиле, в форме регистрации поля ника нет.
   */
  readonly register: (args: {
    email: string;
    password: string;
  }) => Promise<RegisterResult>;
  /** Вход email-аккаунта (services/local-auth.login). */
  readonly login: (args: { email: string; password: string }) => Promise<LoginResult>;
  /**
   * Успешный вход/регистрация (любой email-путь). Вызывающий (auth-modal) закрывает
   * модалку и reconcile'ит account-slot/Профиль (§4.2). `displayName` — ник нового
   * залогиненного (или null). Панель НЕ держит logged-in-state (выход — в меню chip).
   */
  readonly onAuthSuccess: (displayName: string | null) => void;
}

export interface AuthPanelHandle {
  readonly element: HTMLElement;
  /** Снять слушатели/попап (на размонтировании). */
  unmount(): void;
}

type FormMode = 'login' | 'register';

// Floor читаемости группы плашки (PLAQUE_FS_MIN_PX, 13px) живёт в примитиве-плашке
// (dialog-plaque) — единый минимум для ВСЕЙ группы (провайдеры + сегмент + CTA); тут
// поштучных floor'ов больше нет (размером правит плашка).

// =============================================================================
// Mount
// =============================================================================

/**
 * Смонтировать панель «Аккаунт» (ГОСТЕВОЕ тело). Возвращает handle с `.element`.
 * Рендерит 3 кнопки (Google/Discord/Почта) + «Войти по почте» (раскрывает форму).
 * Состояние «залогинен» удалено (выход — в меню account-chip, §3).
 */
export function mountAuthPanel(
  host: HTMLElement,
  opts: AuthPanelOptions,
): AuthPanelHandle {
  const root = document.createElement('section');
  root.className = 'auth-panel';
  root.setAttribute('aria-label', t('auth.panel.sectionAria'));
  host.appendChild(root);

  let formMode: FormMode = 'login'; // default «Вход» (R-3 — возврат частотнее).
  let formExpanded = false;
  let loading = false;
  let passwordVisible = false;
  let destroyed = false;

  let modeSeg: SegmentedHandle | null = null;
  // Group-autofit ВСЕЙ плашки: 3 провайдер-кнопки + переключатель Вход/Регистрация +
  // CTA формы = ОДНА группа → один ИЗМЕРЕННЫЙ размер = min (skill §8.10-B). Правило
  // владеет ПЛАШКА-примитив (dialog-plaque.attachPlaqueGroupAutofit): мы лишь отдаём ей
  // `collect()` — актуальный состав группы при каждом замере (форма раскрывается → состав
  // меняется). Привязка ОДНА на весь mount; refit() после каждого re-render тела.
  let plaqueFit: PlaqueAutofitHandle | null = null;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  function render(): void {
    if (destroyed) return;
    teardownTransient();
    root.innerHTML = '';
    // Заголовок «Аккаунт» / описание / «?» УБРАНЫ (owner-directive 2026-06-12 — «убрать
    // весь описательный текст»): тело модалки = только кнопки (+ форма по «Почта»);
    // заголовок плашки — «Авторизация» в шапке auth-modal.
    renderGuest();
    // Group-uniform autofit ВСЕЙ плашки (провайдеры + сегмент + CTA) — правило владеет
    // ПЛАШКА-примитив. Привязываемся ОДИН раз (на первом render), далее refit() — состав
    // группы re-собирается через collectGroupMembers() при каждом замере (форма
    // раскрылась/режим сменился → состав меняется, но привязка живёт).
    if (!plaqueFit) {
      // followers = лейблы полей («Почта»/«Пароль»/«Ник»): НЕ драйверы группы (их ширина
      // не меряется), но получают ИТОГОВЫЙ размер плашки (followerRatio=1 по умолчанию →
      // «того же размера, что весь остальной текст», owner-directive 2026-06-12). Так они
      // не живут своим токеном «где-то больше, где меньше» — размер диктует ПЛАШКА.
      plaqueFit = attachPlaqueGroupAutofit(root, collectGroupMembers, {
        followers: collectFollowers,
      });
    } else {
      plaqueFit.refit();
    }
  }

  /**
   * ВЕДОМЫЕ плашки — получают её ИТОГОВЫЙ размер (followerRatio=1 → «того же размера, что
   * весь остальной текст»), но НЕ участвуют в group-min (их ширина не меряется):
   *  · лейблы полей «Почта»/«Пароль»/«Ник» (owner-directive 2026-06-12 — не жить своим
   *    токеном, размер диктует плашка);
   *  · подпись CTA «Войти»/«Создать аккаунт» — центрированная пара, измерять её контент-
   *    ширину нельзя (само-референтно → залипание); инвариант «короче длиннейшего
   *    провайдера» гарантирует, что на group-размере она влезает.
   * Re-собираются при КАЖДОМ замере (форма раскрывается → состав меняется).
   */
  function collectFollowers(): readonly HTMLElement[] {
    const els = [...root.querySelectorAll<HTMLElement>('.auth-panel-field-label')];
    const ctaLabel = root.querySelector<HTMLElement>('.auth-panel-cta .pill-button-label');
    if (ctaLabel) els.push(ctaLabel);
    return els;
  }

  /**
   * Актуальный состав group-uniform ДРАЙВЕРОВ плашки (тех, чья ширина МЕРЯЕТСЯ → диктует
   * group-min). Re-вызывается плашкой при КАЖДОМ замере (skill §8.10-B) — переживает
   * re-render тела. Каждый член — `GroupMember`: ШИРИНА от текст-элемента, стабильная
   * высотная опора (heightEl) — от его КОНТРОЛА.
   *  · провайдер-кнопки: текст = `.pill-button-label` (flex 1 1 0), опора = сама кнопка
   *    (.auth-panel-provider, min-height --btn-h);
   *  · сегмент-табы Вход/Регистрация: текст = сама кнопка (textContent), опора = она же
   *    (min-height --btn-h) → пара heightEl не нужна.
   * CTA и лейблы полей — НЕ драйверы, а ВЕДОМЫЕ (collectFollowers): получают итоговый
   * размер группы, но их ширина не меряется (CTA — центрированная пара, само-референтна).
   */
  function collectGroupMembers(): readonly GroupMember[] {
    const members: GroupMember[] = [];
    // Провайдер-кнопки (всегда видимы): ширина от лейбла, высота от кнопки.
    for (const btn of root.querySelectorAll<HTMLElement>('.auth-panel-provider')) {
      const label = btn.querySelector<HTMLElement>('.pill-button-label');
      if (label) members.push({ el: label, heightEl: btn });
    }
    // Сегмент-табы (только при раскрытой форме): голые кнопки (текст = опора).
    for (const tab of root.querySelectorAll<HTMLElement>('.auth-panel-mode .segmented-item')) {
      members.push(tab);
    }
    // CTA — НЕ драйвер (его подпись центрированной парой, ширину НЕ меряем — иначе
    // само-референтна → залипание на resize-вверх). Он ВЕДОМЫЙ (collectFollowers): берёт
    // размер группы. Инвариант: текст CTA («Войти»/«Создать аккаунт») КОРОЧЕ длиннейшего
    // провайдера («Войти через Discord») при равной ширине кнопок → всегда влезает на
    // group-размере. Драйверы = провайдеры + сегмент.
    return members;
  }

  // ── Состояние «гость» (§2.1) — 3 кнопки + раскрываемая форма ────────────────
  function renderGuest(): void {
    const buttons = document.createElement('div');
    buttons.className = 'auth-panel-buttons';

    const gBtn = makeProviderButton(t('auth.panel.providerGoogle'), 'google', ICON_GOOGLE, () =>
      void opts.onGoogle(),
    );
    const dBtn = makeProviderButton(t('auth.panel.providerDiscord'), 'discord', ICON_DISCORD, () =>
      void opts.onDiscord(),
    );
    const eBtn = makeProviderButton(t('auth.panel.providerEmail'), 'email', ICON_MAIL, () => {
      formExpanded = !formExpanded;
      render();
    });
    eBtn.setAttribute('aria-expanded', String(formExpanded));
    buttons.append(gBtn, dBtn, eBtn);
    root.appendChild(buttons);

    // Размер подписей провайдеров НЕ настраивается тут поштучно: их (вместе с сегментом
    // и CTA) включает в СВОЮ group-uniform группу плашка-примитив через
    // collectGroupMembers() (см. render() → attachPlaqueGroupAutofit). Один измеренный
    // размер = min по всей плашке (skill §8.10-B; «равны ПОТОМУ ЧТО велит плашка»).

    if (formExpanded) renderEmailForm();
  }

  // Кнопка-провайдер = примитив pill-button в НЕЙТРАЛЬНОМ варианте (owner-decision
  // 2026-06-12 A3): фон --panel-2 + рамка --line + текст --ink; кнопки различаются
  // ТОЛЬКО цветной ведущей иконкой слева (Google многоцвет / Discord блюрпл / Почта
  // accent), не accent-заливкой. provider-класс — hook (раскладка иконки слева +
  // accent-цвет иконки Почты в auth-panel.css).
  function makeProviderButton(
    label: string,
    provider: 'google' | 'discord' | 'email',
    icon: string,
    onClick: () => void,
  ): HTMLButtonElement {
    const btn = createPillButton({
      icon,
      label,
      variant: 'neutral',
      className: `auth-panel-provider auth-panel-provider--${provider}`,
      onClick,
    }).element;
    btn.disabled = loading;
    return btn;
  }

  // ── Раскрытая email-форма (§2.2/§2.3) ──────────────────────────────────────
  function renderEmailForm(): void {
    const form = document.createElement('form');
    form.className = 'auth-panel-form';
    form.noValidate = true;

    // Переключатель Вход/Регистрация (segmented — общий примитив).
    const segHost = document.createElement('div');
    segHost.className = 'auth-panel-mode-host';
    modeSeg = createSegmented({
      items: [
        { id: 'login', label: t('auth.panel.tabLogin') },
        { id: 'register', label: t('auth.panel.tabRegister') },
      ],
      activeId: formMode,
      ariaLabel: t('auth.panel.modeAria'),
      className: 'auth-panel-mode',
      // selfAutofit:false — сегмент В ПЛАШКЕ: свой self-fit НЕ запускает, его лейблы
      // включает в свою group-uniform группу плашка (collectGroupMembers). Двойного
      // autofit на одном элементе нет (owner-law 2026-06-12, segmented.ts ДВА КОНТЕКСТА).
      selfAutofit: false,
      onChange: (id) => {
        formMode = id as FormMode;
        render();
      },
    });
    segHost.appendChild(modeSeg.element);
    form.appendChild(segHost);

    // Поля.
    const emailField = makeField({
      name: 'email',
      label: t('auth.panel.fieldEmail'),
      type: 'email',
      autocomplete: 'email',
      inputmode: 'email',
    });
    form.appendChild(emailField.wrap);

    const passwordField = makePasswordField();
    form.appendChild(passwordField.wrap);

    // Вариант B (owner-decision 2026-06-21): поля НИКА в форме регистрации НЕТ — ник
    // задаётся СТРОГО в Профиле (первый ввод там бесплатно). Сохраняем nickField=null,
    // чтобы общий submit/error-плейсмент-каркас остался без переверстки.
    const nickField: FieldHandle | null = null;

    // Общая ошибка (над CTA).
    const formError = document.createElement('p');
    formError.className = 'auth-panel-form-error';
    formError.setAttribute('role', 'alert');
    formError.hidden = true;
    form.appendChild(formError);

    // CTA = примитив pill-button (accent-вариант: accent-заливка/белый текст). Значок +
    // подпись — ЦЕНТРИРОВАННОЙ ПАРОЙ (база pill-button, как рейловая пилюля «Войти»;
    // owner-directive 2026-06-12 «прижать значок как на рейле и отцентровать»). Глиф —
    // ПО РЕЖИМУ: «Войти» → ICON_LOGIN (дверь+стрелка, единый знак входа с рейлом);
    // «Создать аккаунт» → ICON_USER_PLUS (силуэт+плюс — своё изображение для регистрации).
    // Размер подписи = размер группы плашки: CTA — ВЕДОМЫЙ (collectFollowers), не драйвер.
    const cta = createPillButton({
      icon: formMode === 'register' ? ICON_USER_PLUS : ICON_LOGIN,
      label: formMode === 'register' ? t('auth.panel.ctaRegister') : t('auth.panel.ctaLogin'),
      variant: 'accent',
      className: 'auth-panel-cta',
    }).element;
    cta.type = 'submit'; // pill-button по умолчанию type=button — CTA сабмитит форму.
    cta.disabled = loading;
    if (loading) cta.classList.add('auth-panel-cta--loading');
    form.appendChild(cta);

    // ── Сабмит ────────────────────────────────────────────────────────────
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      if (loading) return; // повторный сабмит заблокирован (AC-16).
      clearFieldErrors([emailField, passwordField, nickField], formError);
      const email = emailField.input.value;
      const password = passwordField.input.value;
      void submit(formMode, { email, password }, {
        email: emailField,
        password: passwordField,
        displayName: nickField,
        form: formError,
        cta,
      });
    });

    root.appendChild(form);
    // Фокус на первое поле при раскрытии (удобство).
    emailField.input.focus();
  }

  // ---------------------------------------------------------------------------
  // Field builders
  // ---------------------------------------------------------------------------
  interface FieldHandle {
    wrap: HTMLElement;
    input: HTMLInputElement;
    error: HTMLElement;
  }

  function makeField(args: {
    name: string;
    label: string;
    type: string;
    autocomplete: string;
    inputmode?: string;
    help?: string;
  }): FieldHandle {
    const wrap = document.createElement('label');
    wrap.className = 'auth-panel-field';
    const labelEl = document.createElement('span');
    labelEl.className = 'auth-panel-field-label';
    labelEl.textContent = args.label;
    const input = document.createElement('input');
    input.className = 'auth-panel-input';
    input.type = args.type;
    input.name = args.name;
    input.setAttribute('autocomplete', args.autocomplete);
    if (args.inputmode) input.setAttribute('inputmode', args.inputmode);
    input.disabled = loading;
    wrap.append(labelEl, input);
    if (args.help) {
      const help = document.createElement('span');
      help.className = 'auth-panel-field-help';
      help.textContent = args.help;
      wrap.appendChild(help);
    }
    const error = document.createElement('span');
    error.className = 'auth-panel-field-error';
    error.setAttribute('role', 'alert');
    error.hidden = true;
    wrap.appendChild(error);
    return { wrap, input, error };
  }

  function makePasswordField(): FieldHandle {
    const wrap = document.createElement('label');
    wrap.className = 'auth-panel-field auth-panel-field--password';
    const labelEl = document.createElement('span');
    labelEl.className = 'auth-panel-field-label';
    labelEl.textContent = t('auth.panel.fieldPassword');

    const inputRow = document.createElement('span');
    inputRow.className = 'auth-panel-password-row';
    const input = document.createElement('input');
    input.className = 'auth-panel-input';
    input.type = passwordVisible ? 'text' : 'password';
    input.name = 'password';
    input.setAttribute(
      'autocomplete',
      formMode === 'register' ? 'new-password' : 'current-password',
    );
    input.disabled = loading;

    // Глаз show/hide — тактильная цель ≥ --btn-h, SVG-глиф (не текст).
    const eye = document.createElement('button');
    eye.type = 'button';
    eye.className = 'auth-panel-eye';
    eye.setAttribute('aria-label', passwordVisible ? t('auth.panel.hidePassword') : t('auth.panel.showPassword'));
    eye.setAttribute('aria-pressed', String(passwordVisible));
    eye.innerHTML = passwordVisible ? ICON_EYE_OFF : ICON_EYE;
    eye.disabled = loading;
    eye.addEventListener('click', () => {
      passwordVisible = !passwordVisible;
      input.type = passwordVisible ? 'text' : 'password';
      eye.setAttribute('aria-pressed', String(passwordVisible));
      eye.setAttribute('aria-label', passwordVisible ? t('auth.panel.hidePassword') : t('auth.panel.showPassword'));
      eye.innerHTML = passwordVisible ? ICON_EYE_OFF : ICON_EYE;
      input.focus();
    });

    inputRow.append(input, eye);
    wrap.append(labelEl, inputRow);
    const error = document.createElement('span');
    error.className = 'auth-panel-field-error';
    error.setAttribute('role', 'alert');
    error.hidden = true;
    wrap.appendChild(error);
    return { wrap, input, error };
  }

  // ---------------------------------------------------------------------------
  // Submit + error placement
  // ---------------------------------------------------------------------------
  async function submit(
    mode: FormMode,
    values: { email: string; password: string },
    els: {
      email: FieldHandle;
      password: FieldHandle;
      displayName: FieldHandle | null;
      form: HTMLElement;
      cta: HTMLButtonElement;
    },
  ): Promise<void> {
    setLoading(true, els);

    // Вариант B: register БЕЗ ника (ник задаётся в Профиле).
    const result: RegisterResult | LoginResult =
      mode === 'register'
        ? await opts.register({
            email: values.email,
            password: values.password,
          })
        : await opts.login({ email: values.email, password: values.password });

    if (destroyed) return;

    if (result.ok) {
      // Успех → auth-modal закроется + reconcile slot/Профиль через onAuthSuccess
      // (§4.2). Без промежуточного экрана (I-1 / AC-3).
      opts.onAuthSuccess(result.displayName);
      return;
    }

    setLoading(false, els);
    const mapped =
      mode === 'register'
        ? registerErrorMap()[(result as { reason: RegisterErrorReason }).reason]
        : loginErrorMap()[(result as { reason: LoginErrorReason }).reason];
    showError(mapped, els);
  }

  function setLoading(
    isLoading: boolean,
    els: { email: FieldHandle; password: FieldHandle; displayName: FieldHandle | null; cta: HTMLButtonElement },
  ): void {
    loading = isLoading;
    els.email.input.disabled = isLoading;
    els.password.input.disabled = isLoading;
    if (els.displayName) els.displayName.input.disabled = isLoading;
    els.cta.disabled = isLoading;
    els.cta.classList.toggle('auth-panel-cta--loading', isLoading);
  }

  function showError(
    mapped: MappedError,
    els: { email: FieldHandle; password: FieldHandle; displayName: FieldHandle | null; form: HTMLElement },
  ): void {
    const target =
      mapped.slot === 'email'
        ? els.email
        : mapped.slot === 'password'
          ? els.password
          : mapped.slot === 'displayName'
            ? els.displayName
            : null;
    if (target) {
      target.error.textContent = mapped.text;
      target.error.hidden = false;
      target.input.focus();
      target.input.setAttribute('aria-invalid', 'true');
    } else {
      // Общая ошибка над CTA.
      els.form.textContent = '';
      els.form.hidden = false;
      const msg = document.createElement('span');
      msg.textContent = mapped.text;
      els.form.appendChild(msg);
      if (mapped.offerSwitchToLogin) {
        // «Перейти ко входу» — переключает форму в «Вход», переносит email (AC-5).
        const switchBtn = document.createElement('button');
        switchBtn.type = 'button';
        switchBtn.className = 'auth-panel-switch-link';
        switchBtn.textContent = t('auth.panel.switchToLogin');
        switchBtn.addEventListener('click', () => {
          const carriedEmail = els.email.input.value;
          formMode = 'login';
          render();
          // Перенести email в новое поле (после re-render).
          const emailInput = root.querySelector<HTMLInputElement>(
            '.auth-panel-input[name="email"]',
          );
          if (emailInput) emailInput.value = carriedEmail;
        });
        els.form.appendChild(switchBtn);
      }
    }
  }

  function clearFieldErrors(
    fields: Array<FieldHandle | null>,
    formError: HTMLElement,
  ): void {
    for (const f of fields) {
      if (!f) continue;
      f.error.hidden = true;
      f.error.textContent = '';
      f.input.removeAttribute('aria-invalid');
    }
    formError.hidden = true;
    formError.textContent = '';
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------
  // teardownTransient — снимает то, что пере-создаётся при КАЖДОМ re-render (modeSeg).
  // plaqueFit НЕ трогаем здесь: привязка к плашке ОДНА на весь mount (attach в render
  // один раз, далее refit) — снимается только в unmount, иначе утечка/лишние re-attach.
  function teardownTransient(): void {
    if (modeSeg) {
      modeSeg.destroy();
      modeSeg = null;
    }
  }

  render();

  return {
    element: root,
    unmount(): void {
      destroyed = true;
      teardownTransient();
      // Снять глобальные слушатели group-autofit плашки (VIEWPORT_APPLIED_EVENT на
      // window + ResizeObserver) — иначе утечка после размонтирования модалки.
      if (plaqueFit) {
        plaqueFit.detach();
        plaqueFit = null;
      }
      root.remove();
    },
  };
}
